/**
 * Google and Apple sign-in in speak — which buttons exist where, and the native
 * flows behind them. Port of Club Arena's `lib/native/social-login.ts` (+ the
 * `loginWithApple` / `loginWithGoogleNative` of its auth-store), in production
 * there. Plan, threat model and console setup:
 * docs/speak-plan/50-native-social-login.md (CA: docs/features/native-social-login.md).
 *
 *   |        | iOS app                        | Android app                                                  | Web                    |
 *   |--------|--------------------------------|--------------------------------------------------------------|------------------------|
 *   | Apple  | system sheet (ASAuthorization) | Apple's web flow in a Chrome Custom Tab (`openSecureWindow`) | redirect /api/auth/apple  |
 *   | Google | GIDSignIn                      | Credential Manager (Sign in with Google)                     | redirect /api/auth/google |
 *
 * The plugin (`@capgo/capacitor-social-login`) is reached through the bridge
 * registry (`getPlugin('SocialLogin')`, lib/platform.ts), never by importing its
 * npm package, so the web bundle carries none of it; the types below are a
 * minimal mirror of its `dist/esm/definitions.d.ts` (v8.5).
 *
 * The web redirect links never show inside the app: Google blocks OAuth in a
 * WebView (`disallowed_useragent`), and a session opened in the system browser
 * would land in the browser, not the app (the app's session is a Bearer token).
 *
 * The HTTP calls live in `@orborix/auth-client` (next to `verifyOtp`); every one
 * answers like `otp/verify` and persists the native token the same way.
 *
 * This file is part of the OTA `login-account-deletion` zone
 * (scripts/ota-sensitive-zones.json).
 */
import {
  AuthError,
  completeAppleAndroidSignIn,
  signInWithAppleIdentityToken,
  signInWithGoogleIdToken,
  startAppleAndroidSignIn,
  type OtpVerifyResult,
} from '@orborix/auth-client';
import { getPlugin, isNative, nativePlatform } from '../platform.js';

export type SocialProvider = 'apple' | 'google';

type NativeOs = 'ios' | 'android';

// ── Minimal mirror of the plugin types ───────────────────────────────────────

interface PluginAccessToken {
  token: string;
}

interface PluginAppleResult {
  accessToken: PluginAccessToken | null;
  idToken: string | null;
  profile: {
    user: string;
    email: string | null;
    givenName: string | null;
    familyName: string | null;
  };
  /** Only with `useProperTokenExchange: true` at initialize. */
  authorizationCode?: string;
}

interface PluginGoogleOnlineResult {
  responseType: 'online';
  accessToken: PluginAccessToken | null;
  idToken: string | null;
}

interface PluginGoogleOfflineResult {
  responseType: 'offline';
  serverAuthCode: string;
}

interface SocialLoginPlugin {
  initialize(options: {
    apple?: { clientId?: string; redirectUrl?: string; useProperTokenExchange?: boolean };
    google?: {
      iOSClientId?: string;
      iOSServerClientId?: string;
      webClientId?: string;
      mode?: 'online' | 'offline';
    };
  }): Promise<void>;
  login(options: {
    provider: 'apple';
    options: { scopes?: string[]; nonce?: string };
  }): Promise<{ provider: 'apple'; result: PluginAppleResult }>;
  login(options: {
    provider: 'google';
    options: { scopes?: string[]; nonce?: string; forcePrompt?: boolean };
  }): Promise<{
    provider: 'google';
    result: PluginGoogleOnlineResult | PluginGoogleOfflineResult;
  }>;
  /**
   * Android: opens `authEndpoint` in a Chrome Custom Tab and resolves with the
   * first VIEW intent whose URL starts with `redirectUri`; rejects when the
   * user comes back to the app without one.
   */
  openSecureWindow(options: {
    authEndpoint: string;
    redirectUri: string;
  }): Promise<{ redirectedUri: string }>;
}

// ── What the OS flows return ─────────────────────────────────────────────────

export interface NativeAppleCredential {
  identityToken: string;
  authorizationCode?: string;
  /** RAW nonce; Apple got `sha256hex(nonce)` and put it in the token. */
  nonce: string;
  /** Apple shares the name on the FIRST consent only; absent afterwards. */
  givenName?: string;
  familyName?: string;
}

export interface NativeGoogleCredential {
  idToken: string;
  /** RAW nonce; Google got `sha256hex(nonce)` and put it in the token. */
  nonce: string;
}

/**
 * Non-cancellation failure of the native flow (plugin missing, init failed,
 * empty token, OS error). The UI shows the generic copy for it.
 */
export class NativeSocialLoginError extends Error {
  readonly provider: SocialProvider;
  readonly pluginCode: string | undefined;

  constructor(message: string, provider: SocialProvider, pluginCode?: string) {
    super(message);
    this.name = 'NativeSocialLoginError';
    this.provider = provider;
    this.pluginCode = pluginCode;
  }
}

// ── Config ───────────────────────────────────────────────────────────────────

const PLUGIN_NAME = 'SocialLogin';

function nativeOs(): NativeOs | null {
  if (!isNative) return null;
  const platform = nativePlatform();
  return platform === 'ios' || platform === 'android' ? platform : null;
}

function buildValue(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Google client ids of THIS build (native/build-env/*.env). The web client is
 * the backend's `GOOGLE_CLIENT_ID` (one web client for staging and prod): it
 * becomes the token's `aud` on both platforms. iOS also needs its own iOS
 * client — and its reversed id as a URL scheme in Info.plist, or GIDSignIn
 * crashes (native/scripts/social-login-config.test.mjs keeps them together).
 * `null` = no Google button in this build.
 */
function googleClientIds(os: NativeOs): { webClientId: string; iosClientId?: string } | null {
  const webClientId = buildValue(import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID);
  if (!webClientId) return null;
  if (os === 'android') return { webClientId };
  const iosClientId = buildValue(import.meta.env.VITE_GOOGLE_IOS_CLIENT_ID);
  return iosClientId ? { webClientId, iosClientId } : null;
}

/** Apple on Android needs the backend's Android callback registered at Apple: opt-in per build. */
function appleAndroidEnabled(): boolean {
  return import.meta.env.VITE_APPLE_SIGNIN_ANDROID === 'true';
}

function getSocialLoginPlugin(): SocialLoginPlugin | null {
  return getPlugin<SocialLoginPlugin>(PLUGIN_NAME);
}

/**
 * Can this provider's native button be offered? Only inside the app with the
 * plugin registered, and only with its build config: a provider without it is
 * hidden instead of failing on tap.
 */
export function isNativeSocialLoginAvailable(provider: SocialProvider): boolean {
  const os = nativeOs();
  if (!os || !getSocialLoginPlugin()) return false;
  if (provider === 'google') return googleClientIds(os) !== null;
  return os === 'ios' || appleAndroidEnabled();
}

/**
 * Available native providers in display order. iOS: Apple first (App Store
 * Guideline 4.8 + HIG). Android: Google first — the account every Android user
 * already has on the device; Apple's HIG binds only Apple platforms.
 */
export function nativeSocialProviders(): SocialProvider[] {
  const order: SocialProvider[] = nativeOs() === 'ios' ? ['apple', 'google'] : ['google', 'apple'];
  return order.filter((p) => isNativeSocialLoginAvailable(p));
}

/**
 * Redirect links of the WEB login, in display order (Google, Apple). None
 * inside the app, whatever the flags say. Apple waits for its build flag
 * (`VITE_APPLE_OAUTH_ENABLED=true`): the web build must not show it before the
 * backend has the Apple config (plan 50 § 2.3).
 */
export function webSocialProviders(): SocialProvider[] {
  if (isNative) return [];
  return import.meta.env.VITE_APPLE_OAUTH_ENABLED === 'true' ? ['google', 'apple'] : ['google'];
}

// ── Initialize (once, lazy) ──────────────────────────────────────────────────

let initPromise: Promise<void> | null = null;

function ensureInitialized(plugin: SocialLoginPlugin, os: NativeOs): Promise<void> {
  if (initPromise) return initPromise;
  const ids = googleClientIds(os);
  const options: Parameters<SocialLoginPlugin['initialize']>[0] =
    os === 'ios'
      ? {
          // `useProperTokenExchange: true` without `redirectUrl` does NO exchange
          // on the device: it only makes the plugin return the
          // `authorizationCode` in its own field (the backend exchanges it).
          apple: { useProperTokenExchange: true },
          ...(ids?.iosClientId
            ? {
                google: {
                  iOSClientId: ids.iosClientId,
                  // → the id_token's `aud` is the web client (GIDConfiguration.serverClientID).
                  iOSServerClientId: ids.webClientId,
                  mode: 'online' as const,
                },
              }
            : {}),
        }
      : // Android: Apple does not go through the plugin's Apple provider (see
        // `openAppleAndroidSignIn`), so only Google is initialized.
        // `webClientId` = server client id of Credential Manager → `aud`.
        ids
        ? { google: { webClientId: ids.webClientId, mode: 'online' } }
        : {};
  initPromise = plugin.initialize(options).catch((err: unknown) => {
    // Allow a retry on the next tap instead of caching the broken promise.
    initPromise = null;
    throw err;
  });
  return initPromise;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function isUserCancelled(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  // v8.3+: the plugin rejects every cancellation/dismiss with
  // `code === 'USER_CANCELLED'` (ASAuthorizationError.canceled, GIDSignIn -5,
  // Credential Manager's GetCredentialCancellationException…).
  const code = (err as { code?: unknown }).code;
  if (code === 'USER_CANCELLED') return true;
  // Safety net for plugin paths without the normalized code — including
  // openSecureWindow's "OAuth cancelled or no callback received".
  const message = (err as { message?: unknown }).message;
  return typeof message === 'string' && /cancel(l)?ed/i.test(message);
}

function toNativeError(err: unknown, provider: SocialProvider): NativeSocialLoginError {
  if (err instanceof NativeSocialLoginError) return err;
  const message = err instanceof Error ? err.message : String(err);
  const code =
    err && typeof err === 'object' && typeof (err as { code?: unknown }).code === 'string'
      ? (err as { code: string }).code
      : undefined;
  return new NativeSocialLoginError(message, provider, code);
}

function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

function toHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** 32 random bytes in hex (64 chars) — the raw anti-replay nonce. */
function generateNonce(): string {
  return toHex(randomBytes(32));
}

export async function sha256Hex(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return toHex(new Uint8Array(digest));
}

/**
 * PKCE pair (RFC 7636, S256) for Apple on Android: the challenge goes to the
 * server at `/start`, the verifier stays in this JS context until `/complete`.
 */
export async function createPkcePair(): Promise<{ verifier: string; challenge: string }> {
  const verifier = toBase64Url(randomBytes(32));
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return { verifier, challenge: toBase64Url(new Uint8Array(digest)) };
}

function nonEmpty(value: string | null | undefined): string | undefined {
  const v = value?.trim();
  return v ? v : undefined;
}

// ── OS flows ─────────────────────────────────────────────────────────────────

/** `ASAuthorizationScopeEmail` / `ASAuthorizationScopeFullName` raw values. */
export const APPLE_SCOPES = ['email', 'full_name'] as const;

/**
 * iOS: opens the native Sign in with Apple sheet. `null` if the user cancels
 * (silent); throws `NativeSocialLoginError` on any other failure. Android uses
 * `openAppleAndroidSignIn` instead.
 */
export async function nativeAppleLogin(): Promise<NativeAppleCredential | null> {
  const plugin = getSocialLoginPlugin();
  if (!plugin || nativeOs() !== 'ios') {
    throw new NativeSocialLoginError('SocialLogin plugin unavailable', 'apple');
  }
  const nonce = generateNonce();
  try {
    await ensureInitialized(plugin, 'ios');
    // Apple gets the hash; the raw value only goes to our API. The plugin passes
    // it to ASAuthorizationAppleIDRequest verbatim, so the token's claim is
    // `sha256hex(nonce)` and a leaked token alone cannot be replayed.
    const { result } = await plugin.login({
      provider: 'apple',
      // Scopes are ASAuthorization.Scope RAW values: the plugin casts this array
      // straight to `[ASAuthorization.Scope]` (the cast succeeds for any string),
      // so 'name' would request a scope that does not exist and Apple would
      // never share the name. `full_name` + `email` are also what makes the
      // first-consent sheet offer the name and "Hide My Email".
      options: { scopes: [...APPLE_SCOPES], nonce: await sha256Hex(nonce) },
    });
    const identityToken = nonEmpty(result.idToken);
    if (!identityToken) {
      throw new NativeSocialLoginError('Apple returned no identity token', 'apple');
    }
    const authorizationCode =
      nonEmpty(result.authorizationCode) ?? nonEmpty(result.accessToken?.token);
    const givenName = nonEmpty(result.profile?.givenName);
    const familyName = nonEmpty(result.profile?.familyName);
    return {
      identityToken,
      nonce,
      ...(authorizationCode ? { authorizationCode } : {}),
      ...(givenName ? { givenName } : {}),
      ...(familyName ? { familyName } : {}),
    };
  } catch (err) {
    if (isUserCancelled(err)) return null;
    throw toNativeError(err, 'apple');
  }
}

/**
 * Android: runs Apple's authorize URL (from `/native/apple/android/start`) in a
 * Chrome Custom Tab — the system browser, never a WebView — and resolves with
 * the URL the server bounced back to (`<returnUrl>?handoff=…` or `?error=…`).
 * `null` if the user came back without finishing.
 *
 * Not the plugin's own Apple provider on purpose: its Android redirect mode
 * never checks its `state`, expects tokens in the return URL and keeps them in
 * SharedPreferences (and logcat), and a closed tab leaves it stuck ("Last call
 * is not null") until the app restarts; its "Broadcast Channel" mode is an
 * embedded WebView behind a third-party domain.
 */
export async function openAppleAndroidSignIn(params: {
  authorizeUrl: string;
  returnUrl: string;
}): Promise<string | null> {
  const plugin = getSocialLoginPlugin();
  if (!plugin || nativeOs() !== 'android') {
    throw new NativeSocialLoginError('Apple sign-in on Android unavailable', 'apple');
  }
  try {
    const { redirectedUri } = await plugin.openSecureWindow({
      authEndpoint: params.authorizeUrl,
      redirectUri: params.returnUrl,
    });
    return redirectedUri;
  } catch (err) {
    if (isUserCancelled(err)) return null;
    throw toNativeError(err, 'apple');
  }
}

/**
 * Path of the Android Apple return link (`https://<host>/auth/apple/android-return`,
 * plan 50 § 3.4). `openSecureWindow` consumes it; it is never a route:
 * useNativeShell ignores it, warm or cold (see `isAppleAndroidReturnLink`).
 */
export const APPLE_ANDROID_RETURN_PATH = '/auth/apple/android-return';

/**
 * Is this deep link the Android Apple return link? Routing it would put a
 * single-use credential into the SPA's history and land on an unknown route —
 * warm (it also arrives through `appUrlOpen`) or cold (the process died while
 * the Custom Tab was open: the flow is lost, the user simply taps again).
 */
export function isAppleAndroidReturnLink(rawUrl: string): boolean {
  try {
    const url = new URL(rawUrl);
    return (
      url.protocol === 'https:' && url.pathname.replace(/\/+$/, '') === APPLE_ANDROID_RETURN_PATH
    );
  } catch {
    return false;
  }
}

/**
 * The handoff is a random single-use value (stored server-side by its hash).
 * The contract does not fix its encoding, so accept any URL-safe token of a
 * sane length rather than one exact shape.
 */
const HANDOFF_RE = /^[A-Za-z0-9_-]{32,256}$/;

/** `?error=` codes the backend's Android Apple callback sends back (plan 50 § 3.4). */
const ANDROID_APPLE_RETURN_ERRORS = new Set([
  'cancelled',
  'invalid_token',
  'flow_expired',
  'email_unverified',
  'apple_no_email',
  'oauth_network',
  'rate_limited',
  'not_android',
]);

/** A return link that is not ours or carries nothing usable. */
export const INVALID_RETURN = 'invalid_return';

/** Reads the return link of the Android Apple flow. Pure — unit tested. */
export function parseAppleAndroidReturn(
  redirected: string,
  returnUrl: string,
): { handoff: string } | { error: string } {
  if (!redirected.startsWith(`${returnUrl}?`)) return { error: INVALID_RETURN };
  let url: URL;
  try {
    url = new URL(redirected);
  } catch {
    return { error: INVALID_RETURN };
  }
  const handoff = url.searchParams.get('handoff');
  if (handoff && HANDOFF_RE.test(handoff)) return { handoff };
  const error = url.searchParams.get('error');
  return { error: error && ANDROID_APPLE_RETURN_ERRORS.has(error) ? error : INVALID_RETURN };
}

/**
 * Native Google sign-in (online mode → id_token with `aud` = the web client).
 * iOS: GIDSignIn; Android: Credential Manager's "Sign in with Google" sheet.
 * `null` if the user cancels.
 */
export async function nativeGoogleLogin(): Promise<NativeGoogleCredential | null> {
  const plugin = getSocialLoginPlugin();
  const os = nativeOs();
  if (!plugin || !os || !googleClientIds(os)) {
    throw new NativeSocialLoginError('Google native sign-in unavailable', 'google');
  }
  const nonce = generateNonce();
  try {
    await ensureInitialized(plugin, os);
    const hashedNonce = await sha256Hex(nonce);
    const { result } = await plugin.login({
      provider: 'google',
      // Android: no `scopes` — the default set (openid, email, profile) is what
      // we need, and custom scopes would require the plugin's modified
      // MainActivity contract for an extra consent screen.
      // iOS: `forcePrompt` is REQUIRED. Without it the plugin, when GIDSignIn
      // has a previous sign-in in the keychain (every tap after the first),
      // calls `restorePreviousSignIn` and returns THAT session's id_token —
      // minted for an older nonce (or refreshed without one) — so the backend
      // rightly answers `invalid_token`: the first login worked, every later one
      // failed (plan 50 § 9, 2026-10-22). Forcing the sheet makes GIDSignIn mint
      // a fresh token carrying this attempt's nonce.
      options:
        os === 'ios'
          ? { scopes: ['email', 'profile'], nonce: hashedNonce, forcePrompt: true }
          : { nonce: hashedNonce },
    });
    const idToken = result.responseType === 'online' ? nonEmpty(result.idToken) : undefined;
    if (!idToken) {
      throw new NativeSocialLoginError('Google returned no id token', 'google');
    }
    return { idToken, nonce };
  } catch (err) {
    if (isUserCancelled(err)) return null;
    throw toNativeError(err, 'google');
  }
}

// ── Sign-in (OS flow + our API) ──────────────────────────────────────────────

/**
 * Sign in with Apple in the ANDROID app: `/start` (PKCE challenge) → Apple in a
 * Custom Tab → return link → `/complete` with the verifier that never left this
 * context. `null` when the user backed out.
 */
async function appleAndroidSignIn(): Promise<OtpVerifyResult | null> {
  const { verifier, challenge } = await createPkcePair();
  const { authorizeUrl, returnUrl } = await startAppleAndroidSignIn(challenge);
  const redirected = await openAppleAndroidSignIn({ authorizeUrl, returnUrl });
  if (!redirected) return null;
  const outcome = parseAppleAndroidReturn(redirected, returnUrl);
  if ('error' in outcome) {
    if (outcome.error === 'cancelled') return null;
    // Same shape as an API rejection: the screen maps the code to its copy.
    throw new AuthError('Sign in with Apple failed', outcome.error, 0);
  }
  return completeAppleAndroidSignIn({ handoff: outcome.handoff, verifier });
}

/**
 * The whole native sign-in for one provider: the OS flow, then our API, which
 * answers like `otp/verify` (the native token is already persisted when this
 * resolves). `null` = the user cancelled (show nothing). Throws `AuthError`
 * (backend code in `.code`) or `NativeSocialLoginError` (OS / plugin failure).
 */
export async function signInWithNativeProvider(
  provider: SocialProvider,
): Promise<OtpVerifyResult | null> {
  if (provider === 'google') {
    const cred = await nativeGoogleLogin();
    return cred ? signInWithGoogleIdToken(cred) : null;
  }
  if (nativeOs() === 'android') return appleAndroidSignIn();
  const cred = await nativeAppleLogin();
  // Apple only shares the name on the first authorization; it travels to the
  // backend, which keeps it when the profile name is blank.
  return cred ? signInWithAppleIdentityToken(cred) : null;
}
